import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

import TextArea from 'libs/design-system/src/components/TextArea';

import { useLangStore } from '@app/dashboard/store/lang';
import type { IField } from '@app/dashboard/types/outerLayout/common';
import {
  CREATE_CAMPAIGN_FIELDS,
  ENG_MESSAGE_LIMITS,
  UNICODE_MESSAGE_LIMITS,
} from '@app/dashboard/constants/reach/modals/create-campaign';
import {
  hasArabic,
  hasEmoji,
  isEnglishOnly,
} from '../../../../../utils/create-campaign';

const SmsContent = ({
  fields,
  handleFieldChange,
  isCreateLoading,
  isModalOpen,
}: {
  fields: IField;
  handleFieldChange: Function;
  isCreateLoading: boolean;
  isModalOpen: boolean;
}) => {
  const [messageLimits, setMessageLimits] = useState({
    messageCount: 1,
    finalLimit: ENG_MESSAGE_LIMITS[0],
    shouldPreventAtMaxLength: false,
  });
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const getMessageInfo = (value: string) => {
    const length = value.length;

    const limits =
      hasArabic(value) || hasEmoji(value) || !isEnglishOnly(value)
        ? UNICODE_MESSAGE_LIMITS
        : ENG_MESSAGE_LIMITS;

    const index = limits.findIndex((limit) => length <= limit);

    return {
      messageCount: index === -1 ? limits.length : index + 1,
      finalLimit: index === -1 ? limits[limits.length - 1] : limits[index],
      shouldPreventAtMaxLength: length >= limits[limits.length - 1],
    };
  };

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (/^\s+/.test(value)) return; // prevent leading spaces

    const { messageCount, finalLimit, shouldPreventAtMaxLength } =
      getMessageInfo(value);

    setMessageLimits({ messageCount, finalLimit, shouldPreventAtMaxLength });

    handleFieldChange({
      event: {
        target: {
          name: CREATE_CAMPAIGN_FIELDS.CONTENT,
          value: value.slice(0, finalLimit), // ensure value does not exceed final limit
        },
      },
    });
  };

  useEffect(() => {
    setMessageLimits({
      messageCount: 1,
      finalLimit: ENG_MESSAGE_LIMITS[0],
      shouldPreventAtMaxLength: false,
    });
  }, [isModalOpen]);

  return (
    <TextArea
      name={CREATE_CAMPAIGN_FIELDS.CONTENT}
      label={t(
        'innerLayout.reach.campaigns.createCampaign.labels.campaignSmsDescription'
      )}
      placeholder={t(
        'innerLayout.reach.campaigns.createCampaign.placeholders.campaignSmsDescription'
      )}
      maxLength={messageLimits.finalLimit}
      showCharactersCount
      onChange={onChange}
      helperText={t('innerLayout.reach.campaigns.createCampaign.costMessage', {
        message: messageLimits.messageCount,
      })}
      errorMessage={fields[CREATE_CAMPAIGN_FIELDS.CONTENT].errorMsg}
      variant={
        fields[CREATE_CAMPAIGN_FIELDS.CONTENT].errorMsg ? 'error' : 'default'
      }
      shouldPreventAtMaxLength={messageLimits.shouldPreventAtMaxLength}
      value={fields[CREATE_CAMPAIGN_FIELDS.CONTENT].value}
      defaultValue={fields[CREATE_CAMPAIGN_FIELDS.CONTENT].value}
      lang={lang}
      disabled={isCreateLoading}
    />
  );
};

export default SmsContent;
